/**
 * The pending-account-requests panel on the admin dashboard.
 *
 * Renders a table of pending requests with Approve / Deny buttons.
 * Deny opens a small confirmation with an optional reason textarea
 * — the reason is stored on the AccountRequest row for audit.
 *
 * Uses `usePendingAccountRequests` for the list and the approve /
 * deny mutations for the actions. Pending rows disappear from the
 * list as soon as the mutation completes because both hooks
 * invalidate the `["admin", "account-requests"]` query.
 */

import { useState, type ReactNode } from "react";

import {
  useApproveAccountRequest,
  useDenyAccountRequest,
  usePendingAccountRequests,
  type AccountRequestRow,
} from "@/features/admin/useAccountRequests";

function formatRequestedAt(iso: string): string {
  // Compact format: "Apr 14, 22:07" — admin dashboards just need
  // "when did this come in" legibility, not full ISO.
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PendingRequestsSection() {
  const query = usePendingAccountRequests();
  const [denyingId, setDenyingId] = useState<string | null>(null);

  if (query.isLoading) {
    return <SectionShell>Loading…</SectionShell>;
  }
  if (query.isError) {
    return (
      <SectionShell>
        <div className="text-coral-deep font-sans text-[14px]">
          Couldn't load pending requests. Refresh to retry.
        </div>
      </SectionShell>
    );
  }
  const requests = query.data ?? [];

  return (
    <SectionShell>
      {requests.length === 0 ? (
        <div className="text-ink-muted font-sans text-[14px] italic">
          No pending requests — the queue is empty.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {requests.map((row) => (
            <RequestRow
              key={row.id}
              row={row}
              isDenying={denyingId === row.id}
              onStartDeny={() => setDenyingId(row.id)}
              onCancelDeny={() => setDenyingId(null)}
            />
          ))}
        </div>
      )}
    </SectionShell>
  );
}

function SectionShell({ children }: { children: ReactNode }) {
  return (
    <section aria-labelledby="pending-requests-heading" className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <h2
          id="pending-requests-heading"
          className="font-serif text-display-sm text-ink"
        >
          <em className="italic font-normal text-coral-deep">Pending</em> account requests
        </h2>
      </div>
      <div className="rounded-lg border border-edge bg-surface p-5">{children}</div>
    </section>
  );
}

interface RowProps {
  row: AccountRequestRow;
  isDenying: boolean;
  onStartDeny: () => void;
  onCancelDeny: () => void;
}

function RequestRow({ row, isDenying, onStartDeny, onCancelDeny }: RowProps) {
  const approve = useApproveAccountRequest();
  const deny = useDenyAccountRequest();
  const [reason, setReason] = useState("");

  const pending = approve.isPending || deny.isPending;

  return (
    <div
      data-testid={`pending-request-${row.id}`}
      className="flex flex-col gap-3 border-b border-edge-soft pb-3 last:border-b-0 last:pb-0"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col min-w-0">
          <div className="font-sans text-[15px] font-medium text-ink">
            {row.user_display_name}
          </div>
          <div className="font-mono text-[13px] text-ink-muted">{row.user_email}</div>
          <div className="font-sans text-[12px] text-ink-dim mt-[2px]">
            Requested {formatRequestedAt(row.requested_at)}
          </div>
        </div>
        {!isDenying && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pending}
              onClick={() => approve.mutate({ requestId: row.id })}
              className="px-4 py-2 rounded-md font-sans text-[13px] font-semibold text-white bg-gradient-to-r from-coral to-coral-deep shadow-md disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              {approve.isPending ? "Approving…" : "Approve"}
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={onStartDeny}
              className="px-4 py-2 rounded-md font-sans text-[13px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              Deny
            </button>
          </div>
        )}
      </div>

      {isDenying && (
        <div className="flex flex-col gap-2 p-4 rounded-md bg-coral-soft/30 border border-coral-deep/20">
          <label
            htmlFor={`deny-reason-${row.id}`}
            className="font-sans text-[12px] uppercase tracking-[0.14em] text-coral-deep font-semibold"
          >
            Reason (optional)
          </label>
          <textarea
            id={`deny-reason-${row.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={2000}
            className="font-sans text-[14px] text-ink bg-surface border border-edge rounded-md px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            placeholder="e.g., spam signup"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={deny.isPending}
              onClick={() => {
                const cleaned = reason.trim();
                deny.mutate(
                  cleaned ? { requestId: row.id, reason: cleaned } : { requestId: row.id },
                );
              }}
              className="px-4 py-2 rounded-md font-sans text-[13px] font-semibold text-white bg-gradient-to-r from-coral to-coral-deep shadow-md disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              {deny.isPending ? "Denying…" : "Confirm deny"}
            </button>
            <button
              type="button"
              disabled={deny.isPending}
              onClick={onCancelDeny}
              className="px-4 py-2 rounded-md font-sans text-[13px] font-medium text-ink bg-surface border border-edge hover:border-edge/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
